import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useBlocker } from "react-router-dom";
import { useUIContext } from "../../../../context/UIContext";
import QuestionItem from "./QuestionItem";
import useSurveyQuestions from "../../../../hooks/useSurveyQuestions";
import styles from "./SurveyThemes.module.css";

export default function MakingSurvey({ selectedSurvey, onUpdateSurvey }) {
  const { t } = useTranslation();
  const location = useLocation();
  const paths = location.pathname.split("/");
  const schoolId = paths[1];
  const { handleToastCenterTop, setToastConfirmModalState } = useUIContext();

  const [currentTheme, setCurrentTheme] = useState("classic");
  const [surveyDescription, setSurveyDescription] = useState("");
  const [editingQuestions, setEditingQuestions] = useState({}); // 수정 중인 질문들의 임시 상태
  const [draggedQuestion, setDraggedQuestion] = useState(null); // 드래그 중인 질문
  const [questionOrder, setQuestionOrder] = useState([]); // 질문 순서 상태
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // 🔥 변경사항 추적

  // API 호출
  const {
    surveyQuestionsQuery,
    createOrUpdateSurveyQuestions,
    deleteSurveyQuestion,
    checkQuestionHasAnswers,
  } = useSurveyQuestions({
    schoolid: schoolId,
    survey_id: selectedSurvey?.id,
  });

  // useMemo로 questions 최적화
  const questions = useMemo(() => {
    // console.log("selectedSurvey", selectedSurvey);
    // console.log("surveyQuestionsQuery?.data", surveyQuestionsQuery?.data);

    return surveyQuestionsQuery?.data || [];
  }, [surveyQuestionsQuery?.data]);

  const isLoading = surveyQuestionsQuery?.isLoading ?? false;
  const isError = surveyQuestionsQuery?.error ?? null;

  // 각 mutation의 로딩 상태 (방어 코드 추가)
  const isCreatingOrUpdating =
    createOrUpdateSurveyQuestions?.isLoading ?? false;
  const isDeleting = deleteSurveyQuestion?.isLoading ?? false;

  // 표시할 질문 목록 (순서 상태가 있으면 그것을, 없으면 서버 데이터를 정렬해서)
  const displayQuestions = useMemo(() => {
    let baseQuestions;
    if (questionOrder.length > 0) {
      baseQuestions = questionOrder;
    } else {
      baseQuestions = [...questions].sort((a, b) => a.order - b.order);
    }

    // editingQuestions와 합쳐서 실제 표시용 데이터 반환
    return baseQuestions.map((question) => ({
      ...question,
      ...editingQuestions[question.id],
    }));
  }, [questions, questionOrder, editingQuestions]);

  const themes = [
    {
      key: "classic",
      name: t("common.survey.classic"),
      description: t("common.survey.classicDescription"),
    },
    {
      key: "modern",
      name: t("common.survey.modern"),
      description: t("common.survey.modernDescription"),
    },
    {
      key: "minimal",
      name: t("common.survey.minimal"),
      description: t("common.survey.minimalDescription"),
    },
  ];

  // 🔥 변경사항이 있는지 확인하는 함수
  const checkForUnsavedChanges = useCallback(() => {
    if (!selectedSurvey) return false;

    // 1. 편집 중인 질문이 있는지 확인
    const hasEditingQuestions = Object.keys(editingQuestions).length > 0;

    // 2. 새로 추가된 질문이 있는지 확인 (음수 ID)
    const hasNewQuestions = displayQuestions.some((q) => q.id < 0);

    // 3. 설문 설명이 변경되었는지 확인
    const hasDescriptionChange =
      surveyDescription !== (selectedSurvey?.description || "");

    // 4. 질문 순서가 변경되었는지 확인
    const originalOrder = [...questions].sort((a, b) => a.order - b.order);
    const hasOrderChange =
      questionOrder.length > 0 &&
      JSON.stringify(questionOrder.map((q) => q.id)) !==
        JSON.stringify(originalOrder.map((q) => q.id));

    return (
      hasEditingQuestions ||
      hasNewQuestions ||
      hasDescriptionChange ||
      hasOrderChange
    );
  }, [
    editingQuestions,
    displayQuestions,
    surveyDescription,
    selectedSurvey,
    questionOrder,
    questions,
  ]);

  // 🔥 변경사항 상태 업데이트
  useEffect(() => {
    const hasChanges = checkForUnsavedChanges();
    setHasUnsavedChanges(hasChanges);
  }, [checkForUnsavedChanges]);

  // 🔥 브라우저 탭/창 닫기 방지
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        const message =
          "저장되지 않은 변경사항이 있습니다. 정말로 페이지를 떠나시겠습니까?";
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // 🔥 React Router 페이지 이동 방지
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      setToastConfirmModalState({
        visible: true,
        message:
          "저장되지 않은 변경사항이 있습니다.\n정말로 페이지를 떠나시겠습니까?",
        onConfirm: () => {
          setHasUnsavedChanges(false);
          blocker.proceed();
        },
        onCancel: () => {
          // 🔥 취소 시 상태 보존하면서 blocker만 리셋
          blocker.reset();
        },
      });
    }
  }, [blocker, setToastConfirmModalState]);

  // 서버 데이터가 변경될 때 로컬 상태 초기화
  useEffect(() => {
    if (questions.length > 0) {
      // 서버 데이터를 순서대로 정렬해서 questionOrder에 설정
      const sortedQuestions = [...questions].sort((a, b) => a.order - b.order);
      setQuestionOrder(sortedQuestions);
    } else {
      // 서버에 질문이 없으면 빈 배열
      setQuestionOrder([]);
    }
  }, [questions]);

  // 선택된 설문이 변경될 때만 초기화 (실제로 다른 설문으로 바뀔 때만)
  const [prevSurveyId, setPrevSurveyId] = useState(null);

  useEffect(() => {
    if (selectedSurvey && selectedSurvey.id !== prevSurveyId) {
      console.log(
        "🔥 새로운 설문 선택으로 description 초기화:",
        selectedSurvey.description
      );
      setCurrentTheme(selectedSurvey.theme || "classic");
      setSurveyDescription(selectedSurvey.description || "");
      setEditingQuestions({}); // 편집 상태 초기화
      setHasUnsavedChanges(false); // 🔥 변경사항 상태도 초기화
      setPrevSurveyId(selectedSurvey.id);
      // questionOrder는 위의 useEffect에서 questions 변경 시 자동으로 처리됨
    } else if (selectedSurvey && selectedSurvey.id === prevSurveyId) {
      // 같은 설문이지만 테마만 변경된 경우
      setCurrentTheme(selectedSurvey.theme || "classic");
    }
  }, [selectedSurvey, prevSurveyId]);

  // 질문 수정 시 임시 상태 업데이트 (자동저장 방지)
  const handleQuestionChange = (id, field, value) => {
    setEditingQuestions((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  // 드래그 앤 드롭 핸들러
  const handleDragStart = (e, question) => {
    setDraggedQuestion(question);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add("drag-over");
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove("drag-over");
  };

  const handleDrop = (e, targetQuestion) => {
    e.preventDefault();

    if (!draggedQuestion || draggedQuestion.id === targetQuestion.id) {
      setDraggedQuestion(null);
      return;
    }

    const draggedIndex = displayQuestions.findIndex(
      (q) => q.id === draggedQuestion.id
    );
    const targetIndex = displayQuestions.findIndex(
      (q) => q.id === targetQuestion.id
    );

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedQuestion(null);
      return;
    }

    // 질문 순서 상태 업데이트
    const reorderedQuestions = [...displayQuestions];
    const [removed] = reorderedQuestions.splice(draggedIndex, 1);
    reorderedQuestions.splice(targetIndex, 0, removed);

    // 순서 값 업데이트
    const updatedQuestions = reorderedQuestions.map((question, index) => ({
      ...question,
      order: index + 1,
    }));

    setQuestionOrder(updatedQuestions);
    setDraggedQuestion(null);

    // 사용자에게 피드백
    handleToastCenterTop(
      t("common.survey.questionOrderChanged"),
      2000,
      "#28a745"
    );
  };

  const handleDragEnd = () => {
    setDraggedQuestion(null);
  };

  const addQuestion = () => {
    if (!selectedSurvey) return;

    // 임시 ID 생성 (음수로 해서 서버 ID와 구분)
    const tempId = -Date.now();

    // 마지막 질문의 order 찾기 (없으면 0)
    const maxOrder =
      displayQuestions.length > 0
        ? Math.max(...displayQuestions.map((q) => q.order || 0))
        : 0;

    const newQuestion = {
      id: tempId,
      schoolid: schoolId,
      survey_id: selectedSurvey.id,
      question_text: t("common.survey.questionPlaceholder"),
      question_type: "short_answer",
      question_description: null,
      input_type: "text",
      is_required: false,
      is_common: true, // 공통 질문이므로 true로 설정
      subject_id: null,
      reflect_in_document: false,
      order: maxOrder + 1,
      extra_reason: null,
      is_active: true, // 기본값은 활성 상태
      isNew: true, // 새로 추가된 질문임을 표시
    };

    // 질문 순서 상태에 직접 추가
    setQuestionOrder([...displayQuestions, newQuestion]);

    handleToastCenterTop(t("common.survey.questionAdded"), 2000, "#28a745");
  };

  const updateQuestion = (questionId, field, value) => {
    // 질문 타입이 객관식/체크박스로 변경되고 옵션이 없는 경우 기본 옵션 추가
    let finalValue = value;
    if (field === "question_type") {
      if (value === "multiple_choice" || value === "checkbox") {
        // 객관식/체크박스로 변경 시 옵션이 없으면 기본 옵션 추가
        const currentQuestion = displayQuestions.find(
          (q) => q.id === questionId
        );
        if (!currentQuestion?.options || currentQuestion.options.length === 0) {
          handleQuestionChange(questionId, "options", [
            {
              id: -Date.now(), // 임시 ID 추가
              schoolid: schoolId, // schoolid 추가
              option_text: t("common.survey.optionPlaceholder") + " 1",
              order: 1,
            },
          ]);
        }
      } else if (value === "short_answer" || value === "long_answer") {
        // 단답형/장문형으로 변경 시 옵션 초기화
        handleQuestionChange(questionId, "options", []);
      }
    }

    // 임시 상태에 저장 (서버 통신 없음)
    handleQuestionChange(questionId, field, finalValue);

    // questionOrder 상태도 업데이트 (화면 즉시 반영)
    setQuestionOrder((prevOrder) =>
      prevOrder.map((q) =>
        q.id === questionId ? { ...q, [field]: finalValue } : q
      )
    );
  };

  const deleteQuestion = (questionId) => {
    const questionToDelete = displayQuestions.find((q) => q.id === questionId);
    if (!questionToDelete) return;

    // 새로 추가된 질문 (음수 ID)인 경우 로컬에서만 제거
    if (questionId < 0) {
      setQuestionOrder((prevOrder) =>
        prevOrder.filter((q) => q.id !== questionId)
      );
      setEditingQuestions((prev) => {
        const newState = { ...prev };
        delete newState[questionId];
        return newState;
      });
      handleToastCenterTop(t("common.survey.questionDeleted"), 2000, "#28a745");
      return;
    }

    // 기존 질문인 경우 응답 존재 여부 먼저 확인
    checkQuestionHasAnswers.mutate(
      { question_id: questionId },
      {
        onSuccess: (responseData) => {
          const hasResponses = responseData?.hasResponses || false;
          const responseCount = responseData?.count || 0;

          let warningMessage = `⚠️ "${questionToDelete.question_text}"\n\n${t(
            "common.survey.deleteQuestionWarning"
          )}`;

          if (hasResponses) {
            warningMessage += `\n\n📊 현재 ${responseCount}개의 응답이 있습니다.\n이 모든 응답이 영구적으로 삭제됩니다!`;
          }

          warningMessage += `\n\n${t("common.survey.deleteQuestionConfirm")}`;

          // 확인 대화상자 표시
          setToastConfirmModalState({
            visible: true,
            message: warningMessage,
            onConfirm: async () => {
              try {
                // 서버에서 질문 삭제
                await deleteSurveyQuestion.mutateAsync(questionId);

                // 로컬 상태에서도 제거
                setQuestionOrder((prevOrder) =>
                  prevOrder.filter((q) => q.id !== questionId)
                );
                setEditingQuestions((prev) => {
                  const newState = { ...prev };
                  delete newState[questionId];
                  return newState;
                });

                handleToastCenterTop(
                  t("common.survey.questionDeleted"),
                  3000,
                  "#28a745"
                );
              } catch (error) {
                console.error("질문 삭제 오류:", error);
                handleToastCenterTop(
                  t("common.survey.failDelete") + ": " + error.message,
                  3000,
                  "#dc3545"
                );
              }
            },
            onCancel: () => {
              // 취소 시 아무것도 하지 않음
            },
          });
        },
        onError: (error) => {
          console.error("응답 확인 오류:", error);
          // 응답 확인에 실패해도 기본 경고 메시지로 진행
          setToastConfirmModalState({
            visible: true,
            message: `⚠️ "${questionToDelete.question_text}"\n\n${t(
              "common.survey.deleteQuestionWarning"
            )}\n\n${t("common.survey.deleteQuestionConfirm")}`,
            onConfirm: async () => {
              try {
                await deleteSurveyQuestion.mutateAsync(questionId);

                setQuestionOrder((prevOrder) =>
                  prevOrder.filter((q) => q.id !== questionId)
                );
                setEditingQuestions((prev) => {
                  const newState = { ...prev };
                  delete newState[questionId];
                  return newState;
                });

                handleToastCenterTop(
                  t("common.survey.questionDeleted"),
                  3000,
                  "#28a745"
                );
              } catch (error) {
                console.error("질문 삭제 오류:", error);
                handleToastCenterTop(
                  t("common.survey.failDelete") + ": " + error.message,
                  3000,
                  "#dc3545"
                );
              }
            },
            onCancel: () => {
              // 취소 시 아무것도 하지 않음
            },
          });
        },
      }
    );
  };

  const copyQuestion = (questionId) => {
    const questionToCopy = displayQuestions.find((q) => q.id === questionId);
    if (!questionToCopy) return;

    // 임시 ID 생성 (음수로 해서 서버 ID와 구분)
    const tempId = -Date.now();

    // 마지막 질문의 order 찾기 (없으면 0)
    const maxOrder =
      displayQuestions.length > 0
        ? Math.max(...displayQuestions.map((q) => q.order || 0))
        : 0;

    const newQuestion = {
      ...questionToCopy,
      id: tempId, // 임시 ID 설정
      question_text:
        questionToCopy.question_text + " (" + t("common.survey.copy") + ")",
      order: maxOrder + 1,
      is_common: true, // 공통 질문이므로 true로 설정
      is_active: true, // 복사된 질문도 기본값은 활성 상태
      isNew: true, // 새로 추가된 질문임을 표시
      // 옵션이 있다면 각 옵션에도 임시 ID 추가
      options:
        questionToCopy.options?.map((option, index) => ({
          ...option,
          id: -(Date.now() + index + 1), // 각 옵션에 고유한 임시 ID
          schoolid: schoolId, // schoolid 추가
        })) || [],
    };

    // 질문 순서 상태에 직접 추가
    setQuestionOrder([...displayQuestions, newQuestion]);

    handleToastCenterTop(t("common.survey.questionAdded"), 2000, "#28a745");
  };

  // 옵션 관련 함수들
  const addOption = (questionId) => {
    const question = displayQuestions.find((q) => q.id === questionId);
    if (!question) return;

    const newOptions = [
      ...(question.options || []),
      {
        id: -Date.now(), // 임시 ID 추가
        schoolid: schoolId, // schoolid 추가
        option_text: `${t("common.survey.optionPlaceholder")} ${
          (question.options?.length || 0) + 1
        }`,
        order: (question.options?.length || 0) + 1,
      },
    ];

    updateQuestion(questionId, "options", newOptions);
  };

  const updateOption = (questionId, optionId, value) => {
    const question = displayQuestions.find((q) => q.id === questionId);
    if (!question || !question.options) return;

    const updatedOptions = question.options.map((opt) =>
      opt.id === optionId ? { ...opt, option_text: value } : opt
    );

    updateQuestion(questionId, "options", updatedOptions);
  };

  const deleteOption = (questionId, optionId) => {
    const question = displayQuestions.find((q) => q.id === questionId);
    if (!question || !question.options) return;

    const updatedOptions = question.options.filter(
      (opt) => opt.id !== optionId
    );
    updateQuestion(questionId, "options", updatedOptions);
  };

  if (!selectedSurvey) {
    return (
      <div className={styles.surveyContainer}>
        <div
          style={{
            padding: "40px",
            textAlign: "center",
            color: "#666",
            border: "2px dashed #ddd",
            borderRadius: "8px",
          }}
        >
          {t("common.survey.selectSurveyMessage")}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.surveyContainer}>
        <div style={{ padding: "40px", textAlign: "center" }}>
          {t("common.survey.loadingSurvey")}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.surveyContainer}>
        <div style={{ padding: "40px", textAlign: "center", color: "#dc3545" }}>
          <div style={{ fontSize: "18px", marginBottom: "10px" }}>❌</div>
          <div>{t("common.survey.loadingData")}</div>
          <button
            style={{ marginTop: "10px", padding: "8px 16px" }}
            onClick={() => surveyQuestionsQuery.refetch()}
          >
            {t("common.survey.retryButton")}
          </button>
        </div>
      </div>
    );
  }

  const themeClass = styles[currentTheme];

  return (
    <div className={styles.surveyContainer}>
      <div className="d-flex justify-content-between">
        <h2 className={styles.surveyTitle}>📋 {selectedSurvey.title}</h2>
        <div className={styles.themeSelector}>
          <span
            style={{
              fontSize: "15px",
              fontWeight: "500",
              marginLeft: "4px",
              marginRight: "4px",
            }}
          >
            {t("common.survey.theme")}:
          </span>
          {themes.map((theme) => (
            <button
              key={theme.key}
              className={`${styles.themeButton} ${
                currentTheme === theme.key ? styles.active : ""
              }`}
              title={theme.description}
              onClick={() => {
                setCurrentTheme(theme.key);
                // 테마 변경을 즉시 서버에 저장
                if (onUpdateSurvey && selectedSurvey) {
                  onUpdateSurvey({ theme: theme.key });
                }
              }}
            >
              {theme.name}
            </button>
          ))}
        </div>
      </div>

      {/* 테마 선택 */}

      <div className={themeClass}>
        {/* 설문 기본 정보 */}
        <div className={styles.surveyInfo}>
          <div className={styles.infoGrid}>
            <p>
              <strong>{t("common.survey.surveyType")}:</strong>{" "}
              {t(`common.survey.${selectedSurvey.survey_type}`)}
            </p>
            <p>
              <strong>{t("common.survey.responseTarget")}:</strong>{" "}
              {t(`common.survey.${selectedSurvey.response_target}`)}
            </p>
            <p>
              <strong>{t("common.survey.isAnonymous")}:</strong>{" "}
              {selectedSurvey.is_anonymous
                ? t("common.survey.yes")
                : t("common.survey.no")}
            </p>
          </div>

          <textarea
            className={styles.descriptionInput}
            placeholder={t("common.survey.surveyDescription")}
            value={surveyDescription}
            onChange={(e) => setSurveyDescription(e.target.value)}
          />
        </div>

        {/* 질문 목록 */}
        {displayQuestions.map((question, index) => (
          <div
            key={question.id}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, question)}
            style={{
              opacity: draggedQuestion?.id === question.id ? 0.5 : 1,
              transition: "opacity 0.2s",
            }}
            className={`${styles.dragContainer} ${
              draggedQuestion?.id === question.id ? styles.dragging : ""
            }`}
          >
            <div
              className={styles.dragHandle}
              title={t("common.survey.dragToReorder")}
              draggable
              onDragStart={(e) => handleDragStart(e, question)}
              onDragEnd={handleDragEnd}
              style={{
                cursor: "grab",
              }}
              onMouseDown={() => {
                // 드래그 시작 시 cursor 변경
                document.body.style.cursor = "grabbing";
              }}
              onMouseUp={() => {
                // 드래그 종료 시 cursor 복원
                document.body.style.cursor = "default";
              }}
            >
              ⋮⋮
            </div>
            <QuestionItem
              question={question}
              index={index}
              onUpdate={updateQuestion}
              onCopy={copyQuestion}
              onDelete={deleteQuestion}
              onAddOption={addOption}
              onUpdateOption={updateOption}
              onDeleteOption={deleteOption}
              themeStyles={styles}
            />
          </div>
        ))}

        {/* 새 질문 추가 버튼 */}
        <button
          className={styles.addQuestionButton}
          onClick={addQuestion}
          disabled={isCreatingOrUpdating}
        >
          {isCreatingOrUpdating
            ? t("common.status.adding")
            : `+ ${t("common.survey.addQuestion")}`}
        </button>

        {/* 저장 버튼 */}
        <div className={styles.actionButtons}>
          <button
            className={styles.saveButton}
            onClick={async () => {
              try {
                // 1. 먼저 설문 description 업데이트 (변경된 경우에만)
                if (surveyDescription !== (selectedSurvey.description || "")) {
                  if (onUpdateSurvey) {
                    onUpdateSurvey({ description: surveyDescription });
                  }
                }

                // 2. 모든 질문 데이터 준비 (새 질문 + 기존 질문)
                const allQuestions = displayQuestions.map((question) => {
                  // 새로운 질문 (음수 ID)인 경우 id와 임시 필드 제거
                  if (question.id < 0) {
                    const { id, isNew, ...questionData } = question;

                    // 옵션이 있으면 옵션에서도 임시 ID 제거
                    if (questionData.options) {
                      questionData.options = questionData.options.map(
                        (option) => {
                          const { id: optionId, ...optionData } = option;
                          return optionData;
                        }
                      );
                    }

                    return questionData;
                  } else {
                    // 기존 질문인 경우 편집된 내용 병합
                    const editedData = editingQuestions[question.id];
                    const updatedQuestion = editedData
                      ? { ...question, ...editedData }
                      : question;

                    // 옵션 처리: 새로운 옵션(음수 ID)은 id 제거, 기존 옵션(양수 ID)은 id 유지
                    if (updatedQuestion.options) {
                      updatedQuestion.options = updatedQuestion.options.map(
                        (option) => {
                          if (option.id < 0) {
                            // 새로운 옵션: id 제거
                            const { id: optionId, ...optionData } = option;
                            return optionData;
                          } else {
                            // 기존 옵션: id 유지
                            return option;
                          }
                        }
                      );
                    }

                    return updatedQuestion;
                  }
                });

                // 3. 모든 질문을 한 번에 서버로 전송
                await createOrUpdateSurveyQuestions.mutateAsync(allQuestions);

                handleToastCenterTop(
                  t("common.survey.surveySaved"),
                  3000,
                  "#28a745"
                );

                // 🔥 저장 성공 시 변경사항 상태 초기화
                setEditingQuestions({});
                setHasUnsavedChanges(false);
              } catch (error) {
                console.error("설문 저장 오류:", error);
                handleToastCenterTop(
                  t("common.survey.failUpdate") + ": " + error.message,
                  3000,
                  "#dc3545"
                );
              }
            }}
            disabled={isCreatingOrUpdating || isDeleting}
          >
            💾{" "}
            {isCreatingOrUpdating || isDeleting
              ? t("common.status.saving")
              : t("common.survey.saveSurvey")}
          </button>
          <button
            className={styles.previewButton}
            onClick={() => {
              // 활성화된 질문들만 필터링
              const activeQuestions = displayQuestions.filter(
                (question) => question.is_active !== false
              );

              // 설문 데이터 준비
              const previewData = {
                survey: {
                  title: selectedSurvey.title,
                  survey_type: selectedSurvey.survey_type,
                  response_target: selectedSurvey.response_target,
                  is_anonymous: selectedSurvey.is_anonymous,
                },
                questions: activeQuestions.map((question) => ({
                  id: question.id,
                  question_text: question.question_text,
                  question_type: question.question_type,
                  question_description: question.question_description,
                  is_required: question.is_required,
                  options:
                    question.options?.map((option) => ({
                      id: option.id,
                      option_text: option.option_text,
                      order: option.order,
                    })) || [],
                })),
                theme: currentTheme,
                description: surveyDescription,
              };

              // SessionStorage에 데이터 저장
              const previewId = Date.now().toString();
              sessionStorage.setItem(
                `surveyPreview_${previewId}`,
                JSON.stringify(previewData)
              );

              // 짧은 URL로 미리보기 페이지 열기
              const previewUrl = `/${selectedSurvey?.schoolid}/teacher/survey-preview?id=${previewId}`;
              window.open(previewUrl, "_blank");
            }}
          >
            👁️ {t("common.survey.preview")}
          </button>
        </div>
      </div>
    </div>
  );
}
